import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Error Boundary & Scroll To Top
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";

// Auth (eagerly loaded)
import AdminLoginPage from "./pages/AdminLoginPage";
import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPassword";
import UnlockAccountPage from "./pages/UnlockAccountPage";

// Protected route guard
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";

// Public website pages (eagerly loaded for instant customer experience)
import Home from "./pages/Home";
import BookingWebsite from "./pages/BookingWebsite";
import Commercial from "./pages/Commercial";
import CommercialQuotes from "./pages/CommercialQuotes";
import Contact from "./pages/Contact";
import MyAccount from "./pages/MyAccount";
import FAQPage from "./pages/FAQPage";
import Services from "./pages/Services";
import AboutUs from "./pages/AboutUs";
import NotFoundPage from "./pages/NotFoundPage";

// Lazy-loaded Admin dashboard pages (code splitting for faster customer page loads)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Customers = lazy(() => import("./pages/Customers"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Bins = lazy(() => import("./pages/Bins"));
const WasteTypes = lazy(() => import("./pages/WasteTypes"));
const HirePricing = lazy(() => import("./pages/HirePricing"));
const AdminCommercial = lazy(() => import("./pages/AdminCommercial"));
const Promotions = lazy(() => import("./pages/Promotions"));
const Reports = lazy(() => import("./pages/Reports"));
const PostcodeZones = lazy(() => import("./pages/PostcodeZones"));
const AdminManagement = lazy(() => import("./pages/AdminManagement"));
const CustomerProfile = lazy(() => import("./pages/CustomerProfile"));

const AdminLoadingFallback = () => (
  <div style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{ width: "32px", height: "32px", border: "3px solid #E2E8F0", borderTopColor: "#F5821F", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* ── Public website ── */}
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/booking" element={<BookingWebsite />} />
          <Route path="/commercial" element={<Commercial />} />
          <Route path="/commercial-quotes" element={<CommercialQuotes />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/loginpage" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/unlock-account" element={<UnlockAccountPage />} />
          <Route path="/my-account" element={<MyAccount />} />
          <Route path="/account" element={<MyAccount />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about-us" element={<AboutUs />} />

          {/* ── Admin login (public) ── */}
          <Route path="/admin/login" element={<AdminLoginPage />} />

          {/* ── Protected admin dashboard routes (Code split / Lazy Loaded) ── */}
          <Route
            path="/dashboard"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <Dashboard />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <Calendar />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <Customers />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <CustomerProfile />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/bookings"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <Bookings />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/bins"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <Bins />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/waste-types"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <WasteTypes />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/hire-pricing"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <HirePricing />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/promotions"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <Promotions />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/commercial"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminCommercial />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <Reports />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/postcode-zones"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <PostcodeZones />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin-management"
            element={
              <ProtectedAdminRoute>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminManagement />
                </Suspense>
              </ProtectedAdminRoute>
            }
          />

          {/* ── 404 Catch-All Page ── */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;